// Reading one appointment of the export: who, where, what status, how much oil.
import type { Appointment, DeclaredItem, Item, Person, StatusRole } from './types';
import { canonZonePath } from './zones';

export const OIL_ITEM_ID = '05c8eba7-7997-490f-ae9f-3f1f5e918c43';
export const CORE_ZONES = ['Tunis', 'Tunis Center', 'Manouba', 'Ben Arous', 'Ariana'];
const CORE_LOWER = new Set(CORE_ZONES.map((z) => z.toLowerCase()));

/** The operating area ("Greater Tunis"), matched on the first segment of the zone path. */
export function isCoreRegion(region: string | null | undefined): boolean {
  return CORE_LOWER.has(String(region ?? '').toLowerCase());
}

export function personName(u: Person | null | undefined): string {
  if (!u) return '—';
  return ((u.firstName || '') + ' ' + (u.lastName || '')).trim() || '—';
}

/** completed = collected, cancelled = ignored, everything else = still to collect. */
export function defaultRoleOf(status: string | null | undefined): StatusRole {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return 'collected';
  if (s === 'cancelled') return 'ignore';
  return 'pending';
}

/** item.name can be an object or the same object JSON-encoded as a string. */
export function itemDisplayName(item: Item | null | undefined): string {
  if (!item) return 'Unknown item';
  const n = item.name;
  if (!n) return 'Unknown item';
  if (typeof n === 'object') return n.en || n.fr || n.ar || 'Unknown item';
  if (typeof n === 'string') {
    try {
      const p: unknown = JSON.parse(n);
      if (p && typeof p === 'object') {
        const o = p as Record<string, string | undefined>;
        return o.en || o.fr || o.ar || n;
      }
      return n;
    } catch {
      return n;
    }
  }
  return String(n);
}

/** French first: rewards are shown in French on the drivers' sheet. */
export function itemNameFr(item: Item | null | undefined): string {
  if (!item || !item.name) return 'Article';
  let n: unknown = item.name;
  if (typeof n === 'string') {
    try {
      n = JSON.parse(n);
    } catch {
      return n as string;
    }
  }
  if (n && typeof n === 'object') {
    const o = n as Record<string, string | undefined>;
    return o.fr || o.en || o.ar || 'Article';
  }
  return String(n);
}

/** The zone path, with its region spelled one way (see zones.ts). */
export function zoneNameOf(a: Appointment): string {
  if (a.zone && a.zone.name) return canonZonePath(a.zone.name.en || a.zone.name.fr || a.zone.name.ar || '—');
  if (a.simpleUser && a.simpleUser.zone && a.simpleUser.zone.name) {
    const n = a.simpleUser.zone.name;
    return canonZonePath(n.en || n.fr || n.ar || '—');
  }
  return '—';
}

/** "Ben Arous, El Mourouj, El Mourouj 4" -> "Ben Arous". */
export function regionOf(a: Appointment): string {
  const z = zoneNameOf(a);
  if (z === '—') return 'Sans zone';
  return z.split(',')[0].trim() || 'Sans zone';
}

/** The rest of the zone path: "El Mourouj, El Mourouj 4". */
export function localityOf(a: Appointment): string {
  const z = zoneNameOf(a);
  if (z === '—') return '';
  return z
    .split(',')
    .slice(1)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(', ');
}

/** The manifest's zone filter: first segment of the export's own zone only. */
export function zoneGroup(a: Appointment): string {
  const z = a.zone && a.zone.name ? a.zone.name.en || a.zone.name.fr || a.zone.name.ar || '—' : '—';
  if (z === '—') return z;
  return canonZonePath(z.split(',')[0].trim());
}
export function ownZoneName(a: Appointment): string {
  return a.zone && a.zone.name ? canonZonePath(a.zone.name.en || a.zone.name.fr || a.zone.name.ar || '—') : '—';
}

export function phoneOf(u: Person | null | undefined): string {
  return u && u.phone ? String(u.phone).trim() : '';
}
export function phoneDigits(p: string | null | undefined): string {
  return String(p ?? '')
    .replace(/\D/g, '')
    .replace(/^216(?=\d{8}$)/, '');
}
/** A usable phone has at least 8 digits once the leading 216 is removed. */
export function hasPhone(p: string | null | undefined): boolean {
  return phoneDigits(p).length >= 8;
}
/** "+21698123456" -> "98 123 456" for the drivers. */
export function b2cPhone(u: Person | null | undefined): string {
  return fmtPhone(phoneOf(u));
}
/** "+21698123456" -> "98 123 456"; anything else as it is. */
export function fmtPhone(phone: string | null | undefined): string {
  const p = String(phone ?? '').trim();
  const d = p.replace(/[^\d+]/g, '').replace(/^\+?216/, '').replace(/\D/g, '');
  if (d.length === 8) return d.slice(0, 2) + ' ' + d.slice(2, 5) + ' ' + d.slice(5);
  return p;
}

export const UCO_NAME_RE = /^\s*used\s+cooking\s+oil\s*$/i;
export function isUcoItem(di: DeclaredItem, itemId?: string): boolean {
  return di.itemId === (itemId || OIL_ITEM_ID) || UCO_NAME_RE.test(String(itemDisplayName(di.item)));
}
/** Litres of oil declared: the oil item by id, or any item named "Used Cooking Oil". */
export function ucoQty(a: Appointment, itemId?: string): number {
  if (!Array.isArray(a.declaredItems)) return 0;
  let q = 0;
  a.declaredItems.forEach((di) => {
    if (isUcoItem(di, itemId)) q += Number(di.quantity) || 0;
  });
  return q;
}
/** Quantity of exactly this item id (the Manifest's oil stats). */
export function itemQty(a: Appointment, itemId: string): number {
  if (!Array.isArray(a.declaredItems)) return 0;
  let q = 0;
  a.declaredItems.forEach((di) => {
    if (di.itemId === itemId) q += Number(di.quantity) || 0;
  });
  return q;
}

/** The list of appointments in an export file: a plain array, {data:[…]} or similar. */
export function extractArray(parsed: unknown): Appointment[] | null {
  if (Array.isArray(parsed)) return parsed as Appointment[];
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  for (const k of ['data', 'appointments', 'items', 'results', 'records']) {
    if (Array.isArray(o[k])) return o[k] as Appointment[];
  }
  for (const k of Object.keys(o)) if (Array.isArray(o[k])) return o[k] as Appointment[];
  if (o.id && o.simpleUser) return [parsed as Appointment];
  return null;
}
